import { Trans } from "@lingui/react/macro";
import React from "react";
import * as styles from "./ShipCard.module.scss";
import { VisitingShipFragment } from "types/graphql-types";
import {
  ResponsiveSanityImage,
  Button,
  ButtonsContainer
} from "@silversea-ssc/ui-components-react";
import ReadMore from "components/ReadMore/ReadMore";
import clsx from "clsx";
import { getCurrentVersionAt } from "src/utils/ship";
import { useDateOverride } from "src/utils/date/useDateOverride";
import { toUTCDate } from "src/utils/date";

interface ShipCardProps {
  ship: VisitingShipFragment;
  variant: "right" | "left";
}

const ShipCard: React.FC<React.PropsWithChildren<ShipCardProps>> = ({
  ship,
  variant = "right"
}) => {
  const now = useDateOverride() || toUTCDate(new Date());

  const currentVersion =
    ship.versions && getCurrentVersionAt(ship.versions, now);
  if (!currentVersion) return <></>;

  return (
    <div
      className={clsx(styles.shipCard, {
        [styles.variantRight]: variant == "right",
        [styles.variantLeft]: variant == "left"
      })}
    >
      {ship.card?.image && (
        <ResponsiveSanityImage
          className={styles.image}
          image={ship.card?.image}
          defaultWidth={600}
          defaultHeight={500}
          loading="lazy"
        />
      )}

      <div className={styles.nameContainer}>
        <h3 className={styles.title}>{ship.name}</h3>
        <div
          className={clsx(styles.shipType, {
            [styles.shipTypeExpedition]: currentVersion.shipType == "Expedition"
          })}
        >
          {currentVersion.shipType}
        </div>
      </div>
      <div className={styles.description}>
        <p>{ship.card?.description?.localized}</p>
      </div>
      <div className={styles.techDetailsContainer}>
        <ul className={styles.techDetails}>
          <li className={styles.techDetail}>
            <span className={styles.techDetailLabel}>
              <Trans>Crew Onboard</Trans>
            </span>
            <span className={styles.techDetailValue}>
              {currentVersion.crewCount}
            </span>
          </li>

          <li className={styles.techDetail}>
            <span className={styles.techDetailLabel}>
              <Trans>Guest Capacity</Trans>
            </span>
            <span className={styles.techDetailValue}>
              {currentVersion.passengerCount}
            </span>
          </li>

          <li className={styles.techDetail}>
            <span className={styles.techDetailLabel}>
              <Trans>Suites</Trans>
            </span>
            <span className={styles.techDetailValue}>
              {currentVersion.suiteCount}
            </span>
          </li>

          <li className={styles.techDetail}>
            <span className={styles.techDetailLabel}>
              <Trans>Dining Options</Trans>
            </span>
            <span className={styles.techDetailValue}>
              {currentVersion.diningOptions}
            </span>
          </li>
        </ul>
      </div>
      <div className={styles.facilitiesContainer}>
        <ReadMore
          height="6.5em"
          backgroundColor="white"
          className={styles.facilities}
          openLabel={<Trans>Expand Info</Trans>}
          closeLabel={<Trans>Reduce Info</Trans>}
        >
          <ul>
            {currentVersion.facilities &&
              currentVersion.facilities.map((facility) => (
                <li key={facility?._key}>
                  <span>{facility?.name?.localized}</span>
                </li>
              ))}
          </ul>
        </ReadMore>
      </div>
      <div className={styles.footer}>
        <ButtonsContainer align="left" mobileFullSize>
          <Button
            href={currentVersion.deckPlanPdf?.asset?.url}
            variant="secondary"
            iconName="download"
            target="_blank"
            dataContentPiece="download_deck_plan"
            dataContentParams={{ shipName: ship.name }}
            rel="nofollow noopener"
          >
            <Trans>View Deck Plan</Trans>
          </Button>

          {ship.fullPath && (
            <Button
              href={ship.fullPath}
              variant="primary"
              iconName="arrow-right"
              dataContentPiece="ship_details"
              dataContentParams={{ shipName: ship.name }}
            >
              <Trans>Ship Details</Trans>
            </Button>
          )}
        </ButtonsContainer>
      </div>
    </div>
  );
};

export default ShipCard;
